import React, { Component, ReactNode } from 'react';
import { logError, humanizeError } from '@utils/error';
import { ErrorPage } from '@screens/error';
import i18n from '@i18n';

type BoundaryStrategy = 'page' | 'banner';

interface ErrorBoundaryState {
    hasError: boolean;
    error: Error | null;
}

interface ErrorBoundaryProps {
    children: ReactNode;
    strategy?: BoundaryStrategy;
    title?: string;
    onRetry?: () => void;
    resetKeys?: any[];
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    static defaultProps = { strategy: 'page' };

    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, info: React.ErrorInfo) {
        const route = window.location?.pathname;

        // Developer log
        logError(error, {
            where: 'ErrorBoundary',
            route,
            extra: { componentStack: info.componentStack },
        });

        // Show banner immediately if that's the chosen strategy
        this.maybeShowToast();
    }

    componentDidUpdate(prevProps: ErrorBoundaryProps): void {
        const prevKeys = prevProps.resetKeys || [];
        const nextKeys = this.props.resetKeys || [];
        const changed =
            nextKeys.length !== prevKeys.length || nextKeys.some((k, i) => k !== prevKeys[i]);

        if (this.state.hasError && changed) {
            this.reset();
        }
    }

    componentWillUnmount(): void {
        if (this.props.strategy === 'banner') (this.context as any).clear();
    }

    reset = (): void => {
        if (this.props.strategy === 'banner') (this.context as any).clear();
        this.setState({ hasError: false, error: null });
    };

    handleRetry = (): void => {
        this.props.onRetry?.();
        this.reset();
    };

    private maybeShowToast(): void {
        const { strategy, title } = this.props;
        if (strategy !== 'banner') return;

        const description = humanizeError(this.state.error);
        const header = title || i18n.t('Errors.SectionHeader');
        const message = `${header}: ${description}`;

        // Send error to toast system
        window.dispatchEvent(
            new CustomEvent('error-overlay-component', {
                detail: { message },
            })
        );
    }

    render(): ReactNode {
        const { hasError, error } = this.state;
        const { children, strategy, title, onRetry } = this.props;

        if (!hasError) return children;
        if (strategy === 'banner') return null;

        const description = humanizeError(error);
        const header = title || i18n.t('Errors.SectionHeader');

        return (
            <ErrorPage
                title={header}
                description={description}
                onRetry={onRetry ? this.handleRetry : undefined}
            />
        );
    }
}
